import React, { useContext, useState } from "react";
import ThemeContext from "../context/ThemeContext";
import AuthContext from "../context/AuthContext";
import IntroductionContext from "../context/IntroductionContext";
import LanguageContext from "../context/LanguageContext";
import { Link } from "react-router-dom";
import ContactModal from "./ContactModal";
import AboutModal from "./AboutModal";
import EditHomePageModal from "./EditHomePageModal";
import { getLocalizedTitle, getLocalizedContent, getTextDirection } from "../utils/languageUtils";

export default function Introduction() {
    const { theme } = useContext(ThemeContext);
    const { user } = useContext(AuthContext);
    const { introduction, loading } = useContext(IntroductionContext);
    const { language } = useContext(LanguageContext);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
    const [isEditHomeModalOpen, setIsEditHomeModalOpen] = useState(false);
    
    const handleContactClick = (e) => {
        e.preventDefault();
        setIsContactModalOpen(true);
    };

    const handleAboutClick = (e) => {
        e.preventDefault();
        setIsAboutModalOpen(true);
    };

    const handleCloseContactModal = () => {
        setIsContactModalOpen(false);
    };

    const handleCloseAboutModal = () => {
        setIsAboutModalOpen(false);
    };

    const handleEditHomePage = () => {
        setIsEditHomeModalOpen(true);
    };

    const handleCloseEditHomeModal = () => {
        setIsEditHomeModalOpen(false);
    };
    

    // Use introduction data from API, fallback to hardcoded content
    const title = getLocalizedTitle(
        introduction?.english_title || "Welcome to the Qajar Codices project",
        introduction?.persian_title,
        language
    );
    
    const content = getLocalizedContent(
        introduction?.english_introduction || `Welcome to the Qajar Codices project, an initiative dedicated to the 
intellectual legacy of Iranian thinkers during the long nineteenth 
century. Our primary mission is to make accessible the thought that 
characterized the Qajar era by collecting, digitizing, and transcribing 
Qajar manuscripts.

This platform seeks to provide scholars of modern 
Iran with resources, including transcriptions, translations, audio 
renderings, and annotated glossaries. Through our efforts, we aim to 
preserve and present the voices that shaped Iran's intellectual and 
political evolution before and during the constitutional era.

The initial focus of this project centers on Mirza Agha Khan 
Kermani, a pivotal intellectual, journalist, social critic, and political 
activist of the mid- and late-Qajar period. We are also planning to 
expand our collection to include other influential figures of 
nineteenth-century Iranian thought, such as Mirza Malkum Khan, 
Akhundzadeh, Mostashar al-Dowleh, Talebov, and Maragheei.`,
        introduction?.persian_introduction,
        language
    );

    const leftImageSrc = introduction?.left_image_url || "/images/intro_image_left.png";
    const rightImageSrc = introduction?.right_image_url || "/images/intro_image_right.png";
    
    // Get text direction based on language
    const textDirection = getTextDirection(language);
    
    return (
        <div className="introduction">
            {user && (
                <div className="introduction__edit-section">
                    <button 
                        className="introduction__edit-button" 
                        onClick={handleEditHomePage}
                    >
                        {language === 'persian' ? 'ویرایش صفحه اصلی' : 'Edit home page'}
                    </button>
                </div>
            )}
            <div className="introduction__container">
                {/* Left decorative image */}
                <div className="introduction__image introduction__image--left">
                    <img 
                        src={leftImageSrc} 
                        alt="Decorative Qajar manuscript"
                        onError={(e) => {
                            // Fallback to default image if S3 image fails
                            if (e.target.src !== "/images/intro_image_left.png") {
                                e.target.src = "/images/intro_image_left.png";
                            }
                        }}
                        onLoad={(e) => {
                            // Ensure image stays visible after loading
                            e.target.style.display = 'block';
                            e.target.style.visibility = 'visible';
                            e.target.style.opacity = '1';
                        }}
                    />
                </div>
                
                {/* Center text content */}
                <div className="introduction__content">
                    <h2 
                        className="introduction__heading"
                        dir={textDirection}
                    >
                        {title}
                    </h2>
                    <div 
                        className="introduction__text"
                        dir={textDirection}
                    >
                        {content.split('\n\n').map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                        ))}
                    </div>
                    <div className="introduction__cta">
                        <Link to="/archive" className="introduction__button">
                            {language === 'persian' ? 'دسترسی به آرشیو' : 'Access to Archive'}
                        </Link>
                    </div>
                    <div className="introduction__links">
                        <a 
                            href="#" 
                            className="introduction__link" 
                            onClick={handleAboutClick}
                            dir={textDirection}
                        >
                            {language === 'persian' ? 'درباره ما' : 'About'}
                        </a>
                        <a 
                            href="#" 
                            className="introduction__link" 
                            onClick={handleContactClick}
                            dir={textDirection}
                        >
                            {language === 'persian' ? 'تماس با ما' : 'Contact Us'}
                        </a>
                    </div>
                </div>
                
                {/* Right decorative image */}
                <div className="introduction__image introduction__image--right">
                    <img 
                        src={rightImageSrc} 
                        alt="Decorative Qajar manuscript"
                        onError={(e) => {
                            // Fallback to default image if S3 image fails
                            if (e.target.src !== "/images/intro_image_right.png") {
                                e.target.src = "/images/intro_image_right.png";
                            }
                        }}
                        onLoad={(e) => {
                            // Ensure image stays visible after loading
                            e.target.style.display = 'block';
                            e.target.style.visibility = 'visible';
                            e.target.style.opacity = '1';
                        }}
                    />
                </div>
            </div>
            
            {/* Contact Modal */}
            <ContactModal 
                isOpen={isContactModalOpen} 
                onClose={handleCloseContactModal} 
            />
            
            {/* About Modal */}
            <AboutModal 
                isOpen={isAboutModalOpen} 
                onClose={handleCloseAboutModal} 
            />

            {/* Edit Home Page Modal */}
            <EditHomePageModal 
                isOpen={isEditHomeModalOpen} 
                onClose={handleCloseEditHomeModal} 
            />
        </div>
    );
}